import { useState } from "react";
import { CreditCard, Loader2, AlertTriangle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";
import staioIcon from "@/assets/images/staio-icon.png";

export default function PaymentRequired() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRetryCheckout = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest("POST", "/api/stripe/create-checkout", {
        orgId: (user as any).organizationId,
        email: (user as any).email,
        orgName: (user as any).orgName,
        orgType: "property_management_org",
      });
      if (res.ok) {
        const { url } = await res.json();
        if (url) {
          window.location.href = url;
          return;
        }
      }
      const body = await res.json().catch(() => ({}));
      setError(body.message || "Could not start checkout. Please try again.");
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await apiRequest("POST", "/api/logout");
    queryClient.clear();
    window.location.href = "/";
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-6 p-4"
      style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(35 40% 95%) 50%, hsl(25 30% 94%) 100%)" }}
    >
      <img src={staioIcon} alt="Staio" className="w-16 h-16" />

      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
        <div className="flex justify-center">
          <div className="bg-amber-50 rounded-full p-3">
            <CreditCard className="w-8 h-8 text-amber-600" />
          </div>
        </div>

        <h1 className="text-2xl font-display font-bold text-foreground">Complete your subscription</h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Your account was created but your subscription setup is incomplete. Please complete the checkout to access your Staio dashboard.
        </p>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-left">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-2 pt-2">
          <Button
            onClick={handleRetryCheckout}
            disabled={loading}
            className="w-full"
            data-testid="button-retry-checkout"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Setting up checkout...</>
            ) : (
              <><CreditCard className="w-4 h-4 mr-2" />Complete Subscription Setup</>
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full text-muted-foreground"
            data-testid="button-logout-payment"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center max-w-xs">
        Questions? Contact us at{" "}
        <a href="mailto:info@staio.io" className="text-primary hover:underline">
          info@staio.io
        </a>
      </p>
    </div>
  );
}
